
import { PostsDatabaseManager } from "@/services/database.manager";
import PostsModel from "../models/posts.model";

class RealtimePostCacheManager {
  private static instance: RealtimePostCacheManager;
  private dbName = "PostsCache";
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private storeName = "newPosts";

  private constructor() {
    this.initDB();
  }

  static getInstance(): RealtimePostCacheManager {
    if (!RealtimePostCacheManager.instance) {
      RealtimePostCacheManager.instance = new RealtimePostCacheManager();
    }
    return RealtimePostCacheManager.instance;
  }


  private async initDB() {
    try {
        const dbManager = PostsDatabaseManager.getInstance();
        this.db = await dbManager.initDB();
    } catch (error) {
        console.warn('IndexedDB initialization failed for newPosts:', error);
    }
}

  private serializePost(post: PostsModel): any {
    return JSON.parse(JSON.stringify(post));
  }

  private deserializePost(storedPost: any): PostsModel {
    const model = new PostsModel();
    Object.assign(model, storedPost);
    return model;
  }

  async addNewPost(post: PostsModel): Promise<void> {
    if (!this.db || !post._id) return;

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([this.storeName], "readwrite");
        const store = transaction.objectStore(this.storeName);

        const serializablePost = this.serializePost(post);
        const request = store.put(serializablePost);
        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.warn(`Failed to store new post ${post._id} in IndexedDB.`);
          resolve();
        };
      } catch (error) {
        console.error("Error adding new post to IndexedDB:", error);
        resolve();
      }
    });
  }

  async getAllNewPosts(): Promise<PostsModel[]> {
    if (!this.db) return [];

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([this.storeName], "readonly");
        const store = transaction.objectStore(this.storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          const serializedPosts = request.result || [];
          const posts = serializedPosts.map(this.deserializePost);
          resolve(
            posts.sort((a, b) => (b.modifiedOn || 0) - (a.modifiedOn || 0))
          );
        };
        request.onerror = () => resolve([]);
      } catch (error) {
        console.error("Error getting all new posts from IndexedDB:", error);
        resolve([]);
      }
    });
  }

  async clearNewPosts(): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([this.storeName], "readwrite");
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();

        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => {
          console.warn("Error clearing newPosts cache.");
          resolve();
        };
      } catch (error) {
        console.error("Error clearing newPosts IndexedDB:", error);
        resolve();
      }
    });
  }

  async getNewPostsCount(): Promise<number> {
    if (!this.db) {
      await this.initDB();
      if (!this.db) {
        console.warn("RealtimePostCacheManager DB not available for counting.");
        return 0;
      }
    }

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([this.storeName], "readonly");
        const store = transaction.objectStore(this.storeName);
        const request = store.count();

        request.onsuccess = () => {
          resolve(request.result as number);
        };

        request.onerror = () => {
          console.error("Error counting new posts in IndexedDB.");
          resolve(0);
        };
      } catch (error) {
        console.error("Exception during getNewPostsCount:", error);
        resolve(0);
      }
    });
  }
}

export default RealtimePostCacheManager.getInstance();
